import React, { useCallback, useEffect, useState } from 'react';
import { Leer } from '../components/hilfe/Tipp';
import { Loader2, CalendarCheck, CalendarPlus, Clock } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { PortalKopf, PortalSeite } from '../components/portal/PortalKopf';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const ART = { vor_ort: 'In der Kanzlei', telefon: 'Telefon', video: 'Video' };
const tag = (d) => new Date(`${d}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit', year: 'numeric' });
const fehler = (e) => e?.response?.data?.detail || 'Das hat nicht geklappt';
const karte = 'p-5 bg-white rounded-2xl border border-gray-200';

const Vorschlag = ({ v, onDone, toast }) => {
  const [busy, setBusy] = useState(false);
  const wahl = async (fn, msg) => { setBusy(true); try { await fn(); toast({ title: msg }); onDone(); } catch (e) { toast({ title: fehler(e), variant: 'destructive' }); } finally { setBusy(false); } };
  return (
    <li className={karte} data-testid={`portal-vorschlag-${v.id}`}>
      <p className="text-sm font-medium text-[#1B3A5C]">{v.betreff || 'Terminvorschlag der Kanzlei'}</p>
      <p className="text-xs text-gray-500 mb-3">{ART[v.art]} · {v.dauer_min} Minuten</p>
      <div className="flex flex-wrap gap-2">
        {v.slots.map((s) => (
          <button key={s.index} type="button" disabled={busy} onClick={() => wahl(() => api.post(`/termine/vorschlaege/${v.id}/annehmen`, { index: s.index }), 'Termin bestätigt – Sie erhalten eine E-Mail mit Kalenderdatei')}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-[10px] bg-[#1B3A5C] text-white text-sm hover:bg-[#2A5280] disabled:opacity-50" data-testid={`portal-vorschlag-waehlen-${v.id}-${s.index}`}>
            <CalendarCheck className="w-4 h-4" /> {tag(s.datum)}, {s.uhrzeit} Uhr
          </button>
        ))}
        <button type="button" disabled={busy} onClick={() => wahl(() => api.post(`/termine/vorschlaege/${v.id}/ablehnen`, { text: window.prompt('Welche Zeiten passen Ihnen besser? (optional)') || '' }), 'Die Kanzlei meldet sich mit neuen Vorschlägen')}
          className="h-10 px-4 rounded-[10px] border border-gray-300 text-sm text-[#1B3A5C]" data-testid={`portal-vorschlag-ablehnen-${v.id}`}>Keiner passt</button>
      </div>
    </li>
  );
};

const Anfrage = ({ toast }) => {
  const [f, setF] = useState({ anliegen: '', wunsch: '', art: 'vor_ort' });
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const senden = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post('/termine/anfrage', f); setOk(true); } catch (err) { toast({ title: fehler(err), variant: 'destructive' }); } finally { setBusy(false); }
  };
  if (ok) return <p className={`${karte} text-sm text-emerald-700`} data-testid="portal-terminanfrage-ok">Danke! Die Kanzlei schickt Ihnen Terminvorschläge ins Portal.</p>;
  const inp = 'w-full px-3 py-2.5 border border-gray-200 rounded-[10px] text-sm focus:outline-none focus:border-[#b8893d]';
  return (
    <form onSubmit={senden} className={`${karte} space-y-3`} data-testid="portal-terminanfrage">
      <textarea required rows={3} value={f.anliegen} onChange={(e) => setF({ ...f, anliegen: e.target.value })} placeholder="Worum geht es?" className={inp} data-testid="terminanfrage-anliegen" />
      <input value={f.wunsch} onChange={(e) => setF({ ...f, wunsch: e.target.value })} placeholder="Wunschzeiten, z. B. Di oder Do vormittags" className={inp} data-testid="terminanfrage-wunsch" />
      <div className="flex flex-wrap gap-2">{Object.entries(ART).map(([k, l]) => (
        <button key={k} type="button" onClick={() => setF({ ...f, art: k })} aria-pressed={f.art === k} className={`h-9 px-3 rounded-full text-xs border ${f.art === k ? 'bg-[#1B3A5C] text-white border-[#b8893d]' : 'bg-white text-[#1B3A5C] border-gray-200'}`} data-testid={`terminanfrage-art-${k}`}>{l}</button>
      ))}</div>
      <button type="submit" disabled={busy} className="h-10 px-5 rounded-[10px] bg-gradient-to-b from-[#e0b56a] to-[#b8893d] text-[#0B1724] text-sm font-semibold disabled:opacity-50" data-testid="terminanfrage-senden">Termin anfragen</button>
    </form>
  );
};

export default function PortalTermine() {
  const { toast } = useToast();
  const [d, setD] = useState(null);
  const load = useCallback(() => {
    Promise.all([api.get('/termine/meine').catch(() => ({ data: { items: [] } })), api.get('/termine/vorschlaege').catch(() => ({ data: { items: [] } }))])
      .then(([a, b]) => setD({ termine: a.data.items || [], vorschlaege: b.data.items || [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <PortalSeite testId="portal-termine">
      <Header />
      <main className="pt-[104px] lg:pt-[152px] pb-28">
        <PortalKopf kicker="Termine" titel="Ihre Termine" />
        <div className="max-w-3xl mx-auto px-5 mt-8 space-y-10">
          {!d ? <Loader2 className="w-6 h-6 animate-spin text-[#b8893d] mx-auto" /> : (<>
            {d.vorschlaege.length > 0 && (
              <section className="space-y-3"><h2 className="font-serif text-2xl text-[#1B3A5C]">Bitte wählen Sie einen Termin</h2>
                <ul className="space-y-3">{d.vorschlaege.map((v) => <Vorschlag key={v.id} v={v} onDone={load} toast={toast} />)}</ul></section>
            )}
            <section className="space-y-3"><h2 className="font-serif text-2xl text-[#1B3A5C]">Geplante Termine</h2>
              {!d.termine.length ? <Leer icon={CalendarCheck} kompakt testId="portal-termine-leer" titel="Keine geplanten Termine" text="Einen Termin können Sie unten anfragen – die Kanzlei bestätigt ihn hier." /> : (
                <ul className="space-y-2">{d.termine.map((t) => (
                  <li key={t.termin_id} className={`${karte} flex items-center gap-3`} data-testid={`portal-termin-${t.termin_id}`}>
                    <Clock className="w-5 h-5 text-[#b8893d]" /><span className="text-sm text-[#1B3A5C]">{t.anzeige}<span className="block text-xs text-gray-500">{t.art_label}{t.ort ? ` · ${t.ort}` : ''}</span></span>
                  </li>))}</ul>)}
            </section>
            <section className="space-y-3"><h2 className="font-serif text-2xl text-[#1B3A5C] flex items-center gap-2"><CalendarPlus className="w-5 h-5 text-[#b8893d]" /> Termin anfragen</h2><Anfrage toast={toast} /></section>
          </>)}
        </div>
      </main>
      <Toaster />
    </PortalSeite>
  );
}
